<template>
  <section>
    <div style="display: inline-flex;">
      <p style="font-weight: 700; margin-right: 30px;">Filtros na tabela</p>
      <b-field
        :label="$t('products.category')"
        label-position="on-border"
        style="margin-right: 30px;"
      >
        <b-select
          v-model="category"
          id="category"
          :placeholder="$t('products.selectCategory')"
        >
          <option value="">{{ $t("products.allCategories") }}</option>
          <option v-for="cat in categories" :key="cat._id" :value="cat._id">
            {{ cat.name }}
          </option>
        </b-select>
      </b-field>

      <b-field :label="$t('products.subcategory')" label-position="on-border">
        <b-select
          v-model="subcategory"
          id="subcategory"
          :placeholder="$t('products.selectSubcategory')"
          :disabled="!category"
          :style="
            !category ? 'border: solid 1px lightgray; border-radius: 4px;' : ''
          "
        >
          <option value="">{{ $t("products.allSubcategories") }}</option>
          <option
            v-for="sub in filteredSubcategories"
            :key="sub._id"
            :value="sub._id"
          >
            {{ sub.name }}
          </option>
        </b-select>
      </b-field>
    </div>

    <b-table
      :data="filteredProducts"
      narrowed
      hoverable
      scrollable
      :paginated="true"
      :per-page="50"
      pagination-position="both"
      :debounce-search="500"
    >
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <b-table-column
        field="b"
        :label="$t('products.code')"
        sortable
        searchable
        v-slot="props"
      >
        <span class="adjust-top">
          <b-tooltip
            :label="$t('products.productExclusionTip')"
            position="is-right"
          >
            <b-icon
              icon="minus-circle"
              v-if="props.row.p === 0"
              type="is-danger"
              size="is-small"
            ></b-icon>
          </b-tooltip>
          {{ props.row.b }}
        </span>
      </b-table-column>

      <b-table-column
        field="n"
        :label="$t('products.name')"
        sortable
        searchable
        v-slot="props"
      >
        <span class="adjust-top">{{ props.row.n }}</span>
      </b-table-column>

      <b-table-column
        field="vat"
        :label="$t('products.%vat')"
        sortable
        v-slot="props"
        v-if="storeHasVAT"
      >
        <b-select
          v-model="props.row.vat"
          @focus="registerOriginalVAT(props.row.vat)"
          @blur="updateVAT(props.row)"
          v-if="permissions.manageTaxes"
          :disabled="!permissions.manageTaxes"
        >
          <option v-for="v in getVatValues" :key="v.value" :value="v.value">{{
            v.name
          }}</option>
        </b-select>
      </b-table-column>

      <b-table-column
        field="lc"
        :label="$t('products.cost')"
        sortable
        v-slot="props"
        v-if="permissions.seeProductCostTableColumn"
      >
        <div class="cost-input-wrapper">
          <money
            v-model="props.row.lc"
            v-bind="money"
            :class="{ 'is-cost-saving': isSavingCost(props.row.b) }"
            class="input is-expanded"
            @blur.native="updateCost(props.row)"
            @input.native="updateMargin(props.row, false)"
            @focus.native="registerOriginalCost(props.row.lc)"
            style="width: 100px"
            :disabled="
              !permissions.addUpdatePurchase || isSavingCost(props.row.b)
            "
          ></money>

          <span v-if="isSavingCost(props.row.b)" class="cost-saving-overlay">
            <b-icon
              icon="sync-alt"
              custom-class="fa-spin"
              size="is-small"
              :style="{ color: $primaryColor }"
            ></b-icon>
          </span>
        </div>
      </b-table-column>

      <b-table-column
        field="p"
        :label="$t('products.price')"
        sortable
        v-slot="props"
      >
        <money
          v-model="props.row.p"
          v-bind="money"
          class="input is-expanded"
          @blur.native="updatePrice(props.row)"
          @input.native="updateMargin(props.row, false)"
          @focus.native="registerOriginalPrice(props.row.p)"
          style="width: 100px"
          :disabled="!permissions.addProductToCatalogUpdatePrice"
        ></money>
      </b-table-column>

      <b-table-column
        field="vp"
        :label="$t('products.vipPrice')"
        :visible="
          storeBeingManaged.configs.vipActive &&
            permissions.seeProductVipPriceMerchonii
        "
        sortable
        v-slot="props"
      >
        <money
          v-model="props.row.vp"
          v-bind="money"
          class="input is-expanded"
          @blur.native="updateVipPrice(props.row)"
          @focus.native="registerOriginalVipPrice(props.row.vp)"
          style="width: 100px"
          :disabled="!permissions.updateProductVipPriceMerchonii"
        ></money>
      </b-table-column>

      <b-table-column
        field="m"
        :label="$t('products.margin')"
        sortable
        v-slot="props"
        v-if="permissions.seeProductMarginTableColumn"
      >
        <span class="adjust-top">
          {{
            props.row.m !== Number.NEGATIVE_INFINITY && !isNaN(props.row.m)
              ? `${(props.row.m * 100).toFixed(1)} %`
              : "-"
          }}
        </span>
      </b-table-column>

      <b-table-column
        field="iq"
        :label="$t('products.ideal')"
        sortable
        v-slot="props"
      >
        <b-input
          type="tel"
          v-model="props.row.iq"
          style="width: 60px"
          @blur="updateIdealQty(props.row)"
          @focus="registerOriginalIdealQty(props.row.iq)"
          :disabled="!permissions.updateProductIdealQty"
        ></b-input>
      </b-table-column>

      <b-table-column
        field="cq"
        :label="$t('products.current')"
        sortable
        v-slot="props"
      >
        <b-input
          type="tel"
          v-model="props.row.cq"
          style="width: 60px"
          @blur="updateCurrentQty(props.row)"
          @focus="registerOriginalCurrentQty(props.row.cq)"
          :disabled="!permissions.updateProductCurrentQty"
        ></b-input>

        <!-- <input
          type="tel"
          v-model="props.row.cq"
          class="input has-background-light"
          style="width: 60px; background-color: #f5f5f5; cursor: pointer;"
          @click="currentQtyWarning"
          readonly
        /> -->
      </b-table-column>

      <b-table-column
        field="mq"
        :label="$t('products.criticalStockLevelColumn')"
        sortable
        v-slot="props"
      >
        <div class="has-text-centered">
          <span class="adjust-top">
            {{
              isNaN(props.row.iq)
                ? "0"
                : Math.ceil(props.row.iq * (criticalLevelValue / 100))
            }}
          </span>
        </div>
      </b-table-column>

      <b-table-column
        field="rq"
        :label="$t('products.reposition')"
        sortable
        v-slot="props"
      >
        <div class="has-text-centered">
          <span class="adjust-top">
            {{
              isNaN(props.row.iq - props.row.cq)
                ? ""
                : props.row.iq - props.row.cq
            }}
          </span>
        </div>
      </b-table-column>

      <b-table-column
        field="v"
        :label="$t('products.validity')"
        sortable
        v-slot="props"
      >
        <b-datepicker
          v-model="props.row.v"
          :placeholder="$t('products.validity')"
          trap-focus
          position="is-top-left"
          @input="updateValidity(props.row)"
          :disabled="!permissions.updateProductValidity"
          class="datepicker_validity has-text-centered"
        >
          <b-button
            :label="$t('products.clear')"
            type="is-danger"
            outlined
            @click="clearDate(props.row)"
          />
        </b-datepicker>
      </b-table-column>

      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="sad-cry" size="is-large"></b-icon>
            </p>
            <p>{{ $t("common.tableEmptyMessage") }}</p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import { Money } from "v-money";
import { mask } from "../../mixins/mask";

export default {
  props: [
    "products",
    "storeBeingManaged",
    "criticalStockLevel",
    "savingCostBarcode"
  ],

  mixins: [common, mask],

  components: {
    Money
  },

  data() {
    return {
      isLoading: false,
      money: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2
      },
      initialPrice: 0,
      initialVipPrice: 0,
      initialCost: 0,
      iq: null,
      cq: null,
      vat: null,
      category: null,
      subcategory: null
    };
  },

  methods: {
    registerOriginalPrice(price) {
      this.initialPrice = price;
    },

    registerOriginalVipPrice(price) {
      this.initialVipPrice = price;
    },

    registerOriginalCost(cost) {
      this.initialCost = cost || 0;
    },

    registerOriginalIdealQty(iq) {
      this.iq = iq;
    },

    // delete
    registerOriginalCurrentQty(cq) {
      this.cq = cq;
    },

    registerOriginalVAT(vat) {
      this.vat = vat;
    },

    clearDate(product) {
      product.v = null;
      this.updateValidity(product);
    },

    updateValidity(product) {
      this.$emit("has-changes-to-save");
      this.$emit("update-validity", {
        b: product.b,
        v: product.v
      });
    },

    updateMargin(product, markAsChanged = true) {
      if (markAsChanged) {
        this.$emit("has-changes-to-save");
      }
      product.m = product.p > 0 ? (product.p - product.lc) / product.p : null;
    },

    updateCost(product) {
      const currentCost = product.lc || 0;

      if (currentCost !== this.initialCost) {
        const lc = Number(
          String(currentCost.toFixed(2)).replace(/[^0-9]+/g, "")
        );

        this.$emit("update-cost", {
          product: { ...product, lc }
        });
      }
    },

    currentQtyWarning() {
      this.$buefy.toast.open({
        duration: 5000,
        message: this.$t("products.manageCurrentQtyWarning"),
        position: "is-top",
        type: "is-danger"
      });
    },

    updatePrice(product) {
      if (product.p !== this.initialPrice) {
        this.$emit("has-changes-to-save");
        const p = Number(String(product.p.toFixed(2)).replace(/[^0-9]+/g, ""));

        if (p === 0) {
          if (!this.permissions.removeProductFromCatalog) {
            this.alertError({ message: "ACTION_NOT_AUTHORIZED" });
            return false;
          }

          this.$buefy.dialog.confirm({
            message: this.$t("products.confirmProductRemotion"),
            confirmText: this.$t("common.yes"),
            cancelText: this.$t("common.cancel"),
            onConfirm: () => {
              this.$emit("update-price", {
                b: product.b,
                p
              });
            },
            onCancel: () => {
              product.p = this.initialPrice;
            }
          });
        } else {
          this.$emit("update-price", {
            b: product.b,
            p
          });
        }
      }
    },

    updateVipPrice(product) {
      if (product.vp !== this.initialVipPrice) {
        const vp = Number(
          String(product.vp.toFixed(2)).replace(/[^0-9]+/g, "")
        );

        this.$emit("update-vip-price", {
          b: product.b,
          vp
        });
      }
    },

    updateIdealQty(product) {
      if (product.iq !== this.iq) {
        this.$emit("has-changes-to-save");
        this.$emit("update-ideal-qty", {
          b: product.b,
          iq: product.iq
        });
      }
    },

    // delete
    updateCurrentQty(product) {
      if (product.cq !== this.cq) {
        this.$emit("has-changes-to-save");
        this.$emit("update-current-qty", {
          b: product.b,
          cq: product.cq
        });
      }
    },

    updateVAT(product) {
      if (product.vat !== this.vat) {
        this.$emit("has-changes-to-save");
        this.$emit("update-vat", {
          b: product.b,
          vat: product.vat
        });
      }
    },

    isSavingCost(barcode) {
      return this.savingCostBarcode === barcode;
    }
  },

  computed: {
    ...mapGetters([
      "permissions",
      "payments",
      "storeHasVAT",
      "getVatValues",
      "categories"
    ]),

    criticalLevelValue() {
      if (isNaN(this.criticalStockLevel)) return 0;
      return this.criticalStockLevel;
    },

    filteredSubcategories() {
      if (!this.category) {
        return [];
      } else {
        return (
          this.categories.find(cat => cat._id === this.category)
            .subcategories || []
        );
      }
    },

    filteredProducts() {
      return this.products.filter(product => {
        if (!this.category) return true;
        if (this.category && !this.subcategory)
          return product.c === this.category;
        return product.c === this.category && product.s === this.subcategory;
      });
    }
  },

  watch: {
    // Reset subcategory when category changes
    category() {
      this.subcategory = "";
    }
  },

  mounted() {
    this.money.prefix = `${this.payments.currencySymbol} `;
  }
};
</script>

<style scoped>
.adjust-top {
  position: relative;
  top: 8px;
}

.cost-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.cost-input-wrapper .input.is-cost-saving {
  padding-right: 2rem;
}

.cost-saving-overlay {
  position: absolute;
  top: 50%;
  right: 0.65rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  pointer-events: none;
}

.filters-container {
  margin-bottom: 1rem;
}

.filter-fields {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.filter-fields .field {
  min-width: 200px;
  margin-bottom: 0;
}
</style>

<style>
.datepicker_validity .dropdown .dropdown-menu {
  position: fixed;
  top: 6rem;
  right: 4rem;
  margin: auto;
}
</style>
